import {
  ClockCounterClockwise,
  Plus,
  Subtitles,
  TextT,
  Trash,
  Warning,
} from '@phosphor-icons/react';
import type { ReelEditDto } from '@reelplicate/api-client/model';
import {
  type CaptionPosition,
  type CaptionStyle,
  defaultSubtitles,
  type ReelTimeline,
} from '@reelplicate/flows';
import {
  Badge,
  Button,
  Field,
  Inline,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectGroup,
  SelectHeader,
  SelectItem,
  Stack,
  TextArea,
  Toggle,
} from '@reelplicate/ui';
import type { ReactNode } from 'react';

import {
  type StyleOption,
  useCaptionStyleOptions,
} from '@/features/caption-styles/caption-style-options';

import { formatTime, type Selection, type TimelineAction } from './timeline-state';

const POSITIONS: { value: CaptionPosition; label: string }[] = [
  { value: 'top', label: 'Top' },
  { value: 'center', label: 'Middle' },
  { value: 'bottom', label: 'Bottom' },
];

/**
 * The reel editor's side panel: the selected text, the subtitles, and the
 * saved versions to go back to.
 */
export function ReelInspector({
  workspaceId,
  timeline,
  selected,
  hasSubtitles,
  hasWords,
  versions,
  liveEditId,
  busy,
  dispatch,
  onAddText,
  onRestore,
  children,
}: {
  workspaceId: string;
  timeline: ReelTimeline;
  selected: Selection;
  /** The flow already burned subtitles in. */
  hasSubtitles: boolean;
  /** The reel has voiceover timings to make subtitles from. */
  hasWords: boolean;
  versions: ReelEditDto[];
  liveEditId: string | null;
  busy: boolean;
  dispatch: (action: TimelineAction) => void;
  onAddText: () => void;
  onRestore: (editId: string | null) => void;
  /** The post caption, under the edit. */
  children?: ReactNode;
}) {
  const styles = useCaptionStyleOptions(workspaceId);
  const text =
    selected?.kind === 'text'
      ? timeline.texts.find((t) => t.id === selected.id)
      : undefined;
  const subtitles = timeline.subtitles;

  return (
    <Stack gap={6}>
      <Stack as="section" gap={2} aria-label="Text">
        <Heading icon={TextT} title="Text">
          <Button size="sm" variant="outline" leadingIcon={Plus} onClick={onAddText}>
            Add text
          </Button>
        </Heading>
        {text ? (
          <Stack gap={3} className="bg-bg-subtle rounded-md p-3">
            <Field label="Words" helper="Put a word in *asterisks* to highlight it.">
              <TextArea
                value={text.text}
                maxLength={300}
                onFocus={() => dispatch({ type: 'checkpoint' })}
                onChange={(event) =>
                  dispatch({
                    type: 'updateText',
                    id: text.id,
                    patch: { text: event.target.value },
                    transient: true,
                  })
                }
              />
            </Field>
            <Field label="Position">
              <SegmentedControl
                aria-label="Text position"
                value={text.position}
                onValueChange={(position) =>
                  dispatch({
                    type: 'updateText',
                    id: text.id,
                    patch: { position: position as CaptionPosition },
                  })
                }
              >
                {POSITIONS.map((p) => (
                  <SegmentedControlItem key={p.value} value={p.value}>
                    {p.label}
                  </SegmentedControlItem>
                ))}
              </SegmentedControl>
            </Field>
            <StyleSelect
              label="Text style"
              options={[...styles.presets, ...styles.custom]}
              style={text.style}
              onChange={(style) =>
                dispatch({ type: 'updateText', id: text.id, patch: { style } })
              }
            />
            <Inline gap={2} alignY="center" align="between">
              <span className="text-caption text-text-secondary tabular-nums">
                {formatTime(text.start)} – {formatTime(text.end)} · drag it on the
                timeline
              </span>
              <Button
                size="sm"
                variant="ghost"
                leadingIcon={Trash}
                onClick={() => dispatch({ type: 'removeText', id: text.id })}
              >
                Delete
              </Button>
            </Inline>
          </Stack>
        ) : timeline.texts.length ? (
          <Stack as="ul" gap={1}>
            {timeline.texts.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() =>
                    dispatch({ type: 'select', selection: { kind: 'text', id: t.id } })
                  }
                  className="hover:bg-bg-muted focus-visible:shadow-focus-ring text-body-sm flex w-full items-center gap-2 rounded-md px-3 py-2 text-left outline-none"
                >
                  <span className="text-text-primary min-w-0 flex-1 truncate">
                    {t.text.replaceAll('*', '')}
                  </span>
                  <span className="text-caption text-text-tertiary tabular-nums">
                    {formatTime(t.start)}
                  </span>
                </button>
              </li>
            ))}
          </Stack>
        ) : (
          <p className="text-body-sm text-text-secondary">
            No text on the reel. Add some at the playhead.
          </p>
        )}
      </Stack>

      <Stack
        as="section"
        gap={2}
        aria-label="Subtitles"
        className="border-border-subtle border-t pt-5"
      >
        <Heading icon={Subtitles} title="Subtitles">
          <Toggle
            aria-label="Show subtitles"
            checked={Boolean(subtitles?.enabled)}
            disabled={!hasWords}
            onCheckedChange={(enabled) =>
              dispatch({
                type: 'setSubtitles',
                subtitles: { ...(subtitles ?? defaultSubtitles()), enabled },
              })
            }
          />
        </Heading>
        {!hasWords ? (
          <p className="text-body-sm text-text-secondary">
            This reel has no voiceover timings to make subtitles from.
          </p>
        ) : (
          hasSubtitles && (
            <p className="bg-status-warning-bg text-body-sm text-status-warning-fg flex items-start gap-2 rounded-md px-3 py-2.5">
              <Warning aria-hidden className="mt-0.5 size-4 shrink-0" />
              The flow already added subtitles, so turning these on shows them twice.
            </p>
          )
        )}
        {subtitles?.enabled && (
          <Stack gap={3} className="bg-bg-subtle rounded-md p-3">
            <Field label="Show">
              <SegmentedControl
                aria-label="Subtitle timing"
                value={subtitles.mode}
                onValueChange={(mode) =>
                  dispatch({
                    type: 'setSubtitles',
                    subtitles: { ...subtitles, mode: mode as 'word' | 'line' },
                  })
                }
              >
                <SegmentedControlItem value="word">Word by word</SegmentedControlItem>
                <SegmentedControlItem value="line">A line at a time</SegmentedControlItem>
              </SegmentedControl>
            </Field>
            <Field label="Position">
              <SegmentedControl
                aria-label="Subtitle position"
                value={subtitles.position}
                onValueChange={(position) =>
                  dispatch({
                    type: 'setSubtitles',
                    subtitles: { ...subtitles, position: position as CaptionPosition },
                  })
                }
              >
                {POSITIONS.map((p) => (
                  <SegmentedControlItem key={p.value} value={p.value}>
                    {p.label}
                  </SegmentedControlItem>
                ))}
              </SegmentedControl>
            </Field>
            <StyleSelect
              label="Subtitle style"
              options={[...styles.presets, ...styles.custom]}
              style={subtitles.style}
              onChange={(style) =>
                dispatch({
                  type: 'setSubtitles',
                  // The spoken word keeps its highlight in any style.
                  subtitles: {
                    ...subtitles,
                    style: {
                      ...style,
                      highlight: {
                        enabled: true,
                        color: subtitles.style.highlight.color,
                      },
                    },
                  },
                })
              }
            />
          </Stack>
        )}
      </Stack>

      {versions.length > 0 && (
        <Stack
          as="section"
          gap={2}
          aria-label="Versions"
          className="border-border-subtle border-t pt-5"
        >
          <Heading icon={ClockCounterClockwise} title="Versions" />
          <Stack as="ul" gap={1}>
            {versions.map((version) => (
              <li key={version.id}>
                <Inline gap={2} alignY="center" className="px-1 py-1">
                  <span className="text-body-sm text-text-primary flex-1">
                    Version {version.version}
                    {version.durationSeconds !== null && (
                      <span className="text-text-tertiary tabular-nums">
                        {' '}
                        · {formatTime(version.durationSeconds)}
                      </span>
                    )}
                  </span>
                  {version.id === liveEditId ? (
                    <Badge tone="success" size="sm">
                      On the draft
                    </Badge>
                  ) : version.status === 'DONE' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => onRestore(version.id)}
                    >
                      Restore
                    </Button>
                  ) : (
                    <Badge
                      tone={version.status === 'FAILED' ? 'danger' : 'neutral'}
                      size="sm"
                    >
                      {version.status === 'FAILED' ? 'Failed' : 'Rendering…'}
                    </Badge>
                  )}
                </Inline>
              </li>
            ))}
            <li>
              <Inline gap={2} alignY="center" className="px-1 py-1">
                <span className="text-body-sm text-text-primary flex-1">
                  As the flow made it
                </span>
                {liveEditId === null ? (
                  <Badge tone="success" size="sm">
                    On the draft
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => onRestore(null)}
                  >
                    Restore
                  </Button>
                )}
              </Inline>
            </li>
          </Stack>
        </Stack>
      )}

      {children}
    </Stack>
  );
}

/** Pick a caption style for text or subtitles; a look not in the list reads as Custom. */
function StyleSelect({
  label,
  options,
  style,
  onChange,
}: {
  label: string;
  options: StyleOption[];
  style: CaptionStyle;
  onChange: (style: CaptionStyle) => void;
}) {
  const same = (a: CaptionStyle, b: CaptionStyle) =>
    JSON.stringify({ ...a, highlight: null }) ===
    JSON.stringify({ ...b, highlight: null });
  const current = options.find((option) => same(option.style, style));
  const groups = [
    { label: 'TikTok', items: options.filter((o) => o.source === 'tiktok') },
    { label: 'Instagram', items: options.filter((o) => o.source === 'instagram') },
    { label: 'Your styles', items: options.filter((o) => o.source === 'custom') },
  ].filter((group) => group.items.length);
  return (
    <Field label="Style">
      <Select
        aria-label={label}
        value={current?.ref ?? ''}
        placeholder="Custom look"
        onValueChange={(ref) => {
          const picked = options.find((option) => option.ref === ref);
          if (picked) onChange(picked.style);
        }}
      >
        {groups.map((group) => (
          <SelectGroup key={group.label}>
            <SelectHeader>{group.label}</SelectHeader>
            {group.items.map((option) => (
              <SelectItem key={option.ref} value={option.ref}>
                {option.name}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </Select>
    </Field>
  );
}

function Heading({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof TextT;
  title: string;
  children?: ReactNode;
}) {
  return (
    <Inline gap={2} alignY="center" className="min-h-8">
      <Icon aria-hidden className="text-icon-secondary size-4" />
      <h3 className="text-label-md text-text-primary flex-1">{title}</h3>
      {children}
    </Inline>
  );
}
